import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Check, X, Clock, CheckCircle2, XCircle, Trash2, CalendarDays, ShieldAlert } from "lucide-react";

const STATUS_STYLES = {
  pending: { bg: "bg-accent/20 text-accent-foreground", icon: Clock, label: "Pending" },
  approved: { bg: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Approved" },
  rejected: { bg: "bg-red-100 text-red-800", icon: XCircle, label: "Rejected" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs uppercase tracking-widest ${s.bg}`}>
      <Icon className="w-3 h-3" aria-hidden="true" />
      {s.label}
    </span>
  );
}

function daysBetween(a, b) {
  try {
    const d1 = new Date(a);
    const d2 = new Date(b);
    return Math.max(1, Math.round((d2 - d1) / (1000 * 60 * 60 * 24)) + 1);
  } catch {
    return 1;
  }
}

export default function LeavesPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [decision, setDecision] = useState(null); // { row, action }
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (scope !== "all") params.scope = scope;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/leave-requests", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [scope, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const p = rows.filter((r) => r.status === "pending").length;
    const a = rows.filter((r) => r.status === "approved").length;
    const rj = rows.filter((r) => r.status === "rejected").length;
    return { p, a, rj };
  }, [rows]);

  const doDecision = async () => {
    if (!decision) return;
    setDeciding(true);
    try {
      await api.patch(`/leave-requests/${decision.row.id}/decision`, {
        status: decision.action,
        note,
      });
      toast.success(decision.action === "approved" ? "Leave approved" : "Leave rejected");
      setDecision(null);
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    } finally {
      setDeciding(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/leave-requests/${toDelete.id}`);
      toast.success("Removed");
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="leaves-page">
      <div className="fade-up">
        <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">People</p>
        <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Leave requests</h1>
        <p className="mt-3 text-muted-foreground">
          {isOwner
            ? "You approve manager leaves. Staff leaves can be approved by you or by any manager."
            : "Approve or reject staff leave requests. Manager leaves are decided by the owner."}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-3 gap-4 max-w-3xl">
        <div className="bg-white border border-border p-4 fade-up" data-testid="leaves-stat-pending">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Pending</div>
          <div className="mt-1 font-serif text-3xl">{stats.p}</div>
        </div>
        <div className="bg-white border border-border p-4 fade-up" data-testid="leaves-stat-approved">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Approved</div>
          <div className="mt-1 font-serif text-3xl">{stats.a}</div>
        </div>
        <div className="bg-white border border-border p-4 fade-up" data-testid="leaves-stat-rejected">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Rejected</div>
          <div className="mt-1 font-serif text-3xl">{stats.rj}</div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-4 flex-wrap">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Applicant</Label>
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="mt-1 w-52" data-testid="leaves-scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All (staff + managers)</SelectItem>
              <SelectItem value="staff">Staff only</SelectItem>
              <SelectItem value="manager">Managers only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="mt-1 w-40" data-testid="leaves-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4">
        {loading ? (
          <div className="bg-white border border-border p-10 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-border p-14 text-center">
            <p className="font-serif text-2xl">No leave requests yet.</p>
            <p className="text-muted-foreground mt-2">
              Staff can apply from the kiosk — "Apply for Leave" button on the home screen.
            </p>
          </div>
        ) : (
          rows.map((r) => {
            const days = daysBetween(r.start_date, r.end_date);
            // Managers can NOT decide manager-leaves — only owner can
            const canDecide =
              r.status === "pending" &&
              (isOwner || (!r.is_manager_leave && Boolean(user?.manager_name)));
            return (
              <div key={r.id} className="bg-white border border-border p-6 fade-up" data-testid={`leave-row-${r.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-serif text-2xl tracking-tight">{r.employee_name}</span>
                      <StatusBadge status={r.status} />
                      {r.is_manager_leave && (
                        <span className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full uppercase tracking-widest">
                          <ShieldAlert className="w-3 h-3" aria-hidden="true" />
                          Manager
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
                      <CalendarDays className="w-4 h-4" aria-hidden="true" />
                      <span>
                        <strong className="text-foreground">{r.start_date}</strong> to{" "}
                        <strong className="text-foreground">{r.end_date}</strong> · {days} day{days > 1 ? "s" : ""}
                      </span>
                      <span>· {r.employee_role}</span>
                    </div>
                    {r.reason && <p className="mt-2 text-sm">{r.reason}</p>}
                    <div className="mt-3 text-xs text-muted-foreground">
                      Applied {new Date(r.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      {r.status !== "pending" && (
                        <> · {r.status} by <strong>{r.decided_by || "—"}</strong> on {new Date(r.decided_at).toLocaleDateString()}</>
                      )}
                    </div>
                    {r.note && (
                      <div className="mt-3 text-xs bg-secondary px-3 py-2 rounded">
                        <span className="text-muted-foreground uppercase tracking-widest">Note:</span> {r.note}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {canDecide ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setDecision({ row: r, action: "rejected" }); setNote(""); }}
                          data-testid={`reject-leave-${r.id}`}
                        >
                          <X className="w-4 h-4 mr-1" aria-hidden="true" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => { setDecision({ row: r, action: "approved" }); setNote(""); }}
                          data-testid={`approve-leave-${r.id}`}
                          className="bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          <Check className="w-4 h-4 mr-1" aria-hidden="true" /> Approve
                        </Button>
                      </div>
                    ) : r.status === "pending" && r.is_manager_leave ? (
                      <span className="text-xs text-muted-foreground">Owner only</span>
                    ) : null}
                    {isOwner && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setToDelete(r)}
                        data-testid={`delete-leave-${r.id}`}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent data-testid="leave-decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {decision?.action === "approved" ? "Approve leave" : "Reject leave"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {decision?.row.employee_name} · {decision?.row.start_date} to {decision?.row.end_date}
          </p>
          <div>
            <Label htmlFor="note">Note for the applicant (optional)</Label>
            <Textarea
              id="note"
              data-testid="leave-decision-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={decision?.action === "approved" ? "Enjoy the break." : "Reason for rejection"}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecision(null)}>Cancel</Button>
            <Button
              onClick={doDecision}
              disabled={deciding}
              data-testid="confirm-leave-decision-btn"
              className={
                decision?.action === "approved"
                  ? "rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  : "rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              }
            >
              {deciding ? "Working…" : decision?.action === "approved" ? "Confirm approve" : "Confirm reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif tracking-tight">Remove this leave record?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the record for {toDelete?.employee_name}. Cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-leave-btn" onClick={doDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
